import React, { useEffect, useMemo } from "react";
import { Controller } from "react-hook-form";
import { Grid } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputUI from "../material-ui/InputUI";
import PermissionSections from "./PermissionSections";

// Import shared config
import {
  permissionDependencies,
  documentsDeps,
} from "../../utils/permissionConfig";

const EditRoleForm = ({
  register,
  control,
  setValue,
  watch,
  errors,
  tableValues,
}) => {
  const statusOptions = [
    { name: "Active", value: "active" },
    { name: "Inactive", value: "inactive" },
  ];

  // CLG's get-roles returns the role's permissions as "permission_name";
  // NBL's returns the same comma-separated list as "permissions".
  const initialPermissions = useMemo(() => {
    const assigned = tableValues?.permission_name ?? tableValues?.permissions;
    return assigned
      ? assigned.split(",").map((p) => p.trim()).filter(Boolean)
      : [];
  }, [tableValues]);

  const permissions = watch("permissions");

  useEffect(() => {
    if (permissions.length === 0) {
      setValue("permissions", initialPermissions);
    }
  }, [initialPermissions, setValue]);

  const handleCategoryChange = (category, isChecked) => {
    let newPermissions = [...permissions];
    const categoryValues = category.children.map((child) => child.value);

    let additionalDeps = [];

    if (category.name === "Mail Room") {
      additionalDeps = permissionDependencies["view_documents"] || [];
    }

    if (category.name === "Settings") {
      const settingsRelatedPermissions = [
        "view_users",
        "create_users",
        "edit_users",
        "delete_users",
        "view_field_settings",
        "create_field_settings",
        "edit_field_settings",
        "delete_field_settings",
        "view_document_requests",
        "create_document_requests",
        "edit_document_requests",
        "delete_document_requests",
      ];
      if (isChecked) {
        newPermissions = [
          ...newPermissions,
          ...categoryValues,
          ...settingsRelatedPermissions,
        ];
      } else {
        newPermissions = newPermissions.filter(
          (perm) =>
            !categoryValues.includes(perm) &&
            !settingsRelatedPermissions.includes(perm)
        );
      }
      setValue("permissions", [...new Set(newPermissions)]);
      return;
    }

    if (category.name === "Delete") {
      const deleteRelatedPermissions = [
        "view_trash_users",
        "delete_users_permanently",
        "restore_users",
        "view_trash_documents",
        "delete_documents_permanently",
        "restore_documents",
      ];
      if (isChecked) {
        newPermissions = [
          ...newPermissions,
          ...categoryValues,
          ...deleteRelatedPermissions,
        ];
      } else {
        newPermissions = newPermissions.filter(
          (perm) =>
            !categoryValues.includes(perm) &&
            !deleteRelatedPermissions.includes(perm)
        );
      }
      setValue("permissions", [...new Set(newPermissions)]);
      return;
    }

    if (category.name === "All Documents" && !isChecked) {
      const toRemove = [
        ...categoryValues,
        "view_documents",
        "view_all_assigned",
        "view_all_unassigned",
        "view_my_assigned",
        ...documentsDeps,
      ];
      newPermissions = newPermissions.filter(
        (perm) => !toRemove.includes(perm)
      );
    } else if (isChecked) {
      newPermissions = [
        ...newPermissions,
        ...categoryValues,
        ...additionalDeps,
      ];
    } else {
      newPermissions = newPermissions.filter(
        (perm) =>
          !categoryValues.includes(perm) && !additionalDeps.includes(perm)
      );
    }

    setValue("permissions", [...new Set(newPermissions)]);
  };

  const handlePermissionChange = (permission) => {
    const isChecked = permissions.includes(permission.value);

    let dependencies = [];

    if (permission.value === "view_unprocessed_documents") {
      dependencies = [
        "view_all_assigned",
        "view_all_unassigned",
        "view_my_assigned",
      ];
    } else {
      dependencies = permissionDependencies[permission.value] || [];
    }

    if (isChecked) {
      const updatedPermissions = permissions.filter(
        (p) => p !== permission.value && !dependencies.includes(p)
      );
      setValue("permissions", updatedPermissions);
    } else {
      const updatedPermissions = [
        ...new Set([...permissions, permission.value, ...dependencies]),
      ];
      setValue("permissions", updatedPermissions);
    }
  };

  return (
    <Grid>
      <div className="edit-document-form flex flex-col gap-3">
        {/* Role name + status */}
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-[minmax(0,1fr)_minmax(220px,340px)]">
          <div className="min-w-0">
            <div className="mb-1 text-[13px] font-semibold text-gray-800">
              Role Name <span className="text-red-500">*</span>
            </div>
            <Controller
              name="name"
              defaultValue={tableValues.name}
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputUI
                  name="name"
                  type="text"
                  value={value || ""}
                  setValue={onChange}
                  variant="outlined"
                  fullWidth
                  placeholder="Enter role name"
                  sx={{
                  "& .MuiOutlinedInput-root": { height: "40px", borderRadius: "8px", backgroundColor: "#fff", fontSize: "14px" },
                  "& fieldset": { borderColor: "#e5e7eb" },
                }}
                  register={register}
                />
              )}
            />
            <span className="error-message">{errors["name"]?.message}</span>
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-[13px] font-semibold text-gray-800">Status</div>
            <FormControl fullWidth>
              <Controller
                control={control}
                name="status"
                defaultValue={tableValues.status ?? "inactive"}
                render={({ field: { onChange, value } }) => (
                  <Select
                    variant="outlined"
                    value={value}
                    onChange={onChange}
                    {...register("status")}
                    renderValue={(selected) => (
                      <span className="flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            selected === "active" ? "bg-emerald-500" : "bg-gray-400"
                          }`}
                        />
                        {statusOptions.find((o) => o.value === selected)?.name || selected}
                      </span>
                    )}
                    sx={{
                      height: "40px",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "14px",
                      "& fieldset": { borderColor: "#e5e7eb" },
                    }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <span className="error-message">{errors["status"]?.message}</span>
          </div>
        </div>

        {/* Permissions (same permission state + handlers as before) */}
        <PermissionSections
          permissions={permissions}
          onCategoryChange={handleCategoryChange}
          onPermissionChange={handlePermissionChange}
          error={errors["permissions"]?.message}
        />
      </div>
    </Grid>
  );
};

export default EditRoleForm;
